import { deleteHttp, post, put } from "../../../common/api/apiShared";
import { ProcessingActivityModel, SelectableAgreement, Source } from "../../ProcessingActivity.types";
import { useTranslation } from "../../../localization/useTranslation";
import { ValidationError } from "../../../common/validationError";
import { useSelectableLegalEntityQuery } from "../../../legalEntity/useSelectableLegalEntityQuery";
import { useSelectableDataCategoriesQuery } from "../../hooks/useSelectableDataCategoriesQuery";
import { useSelectableDocumentsQuery } from "../../hooks/useSelectableDocumentQuery";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { UserSelectableItem } from "../../../user/User.types";
import { ProcessingActivitySourcesPermissions } from "../../../auth/userContextProvider/UserContextProvider.types";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useState } from "react";
import { AgreementSaveModel, DataProcessorAgreementSaveModel } from "../agreementDialog/DataProcessorAgreementDialog.types";
import { SelectedLegalEntityDocument } from "../../../legalEntity/legalEntityDocumentTab/LegalEntityDocumentTab.types";
import { useExpandedId } from "../../../common/hooks/useExpandedId";
import { useOptimisticUpdate } from "../../../common/hooks/useOptimisticUpdate";
import { useGroupEntitiesQuery } from "../../hooks/useGroupEntitiesQuery.ts";
import { ContractOnTransfer, SelectableContract, toSelectableAgreement } from "../../../contract/contract/Contract.types.ts";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useSourceMapping(selectedDataCategories: Array<string>, processingActivity: ProcessingActivityModel, sources: Array<Source>) {
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const [dataProcessorAgreementSaveModel, setDataProcessorAgreementSaveModel] = useState<DataProcessorAgreementSaveModel | undefined>(undefined);
    const [sourceId, setSourceId] = useState("");
    const { getExpandedId, setExpandedId } = useExpandedId();
    const optimisticUpdate = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();

    let queryKey = "processingActivity" + processingActivity.id;

    const sourcePermissions = permissions.processingActivitySourcesPermissions;

    let dataCategoriesQuery = useSelectableDataCategoriesQuery();
    let legalEntitiesQuery = useSelectableLegalEntityQuery();
    let documentsQuery = useSelectableDocumentsQuery(
        sourcePermissions.hasAgreementPermission,
        sources.find((x) => x.id === getExpandedId())?.dataControllerId ?? undefined,
        processingActivity.id
    );
    let responsibleQuery = useResponsibleQuery(sourcePermissions.hasResponsiblePermission);
    let groupEntityQuery = useGroupEntitiesQuery();

    const legalEntityIdOnTransfer = processingActivity.sources.find((x) => x.id === getExpandedId())?.dataControllerId ?? undefined;

    const contractsOnSourceUrl = `/processingactivity/source/${getExpandedId()}/contracts`;
    const contractsOnSourceQuery = useDotLegalQuery<ContractOnTransfer>(contractsOnSourceUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });
    const selectableContractsUrl = `/LegalEntity/${legalEntityIdOnTransfer}/selectableContracts`;
    const selectableContractsQuery = useDotLegalQuery<Array<SelectableContract>>(selectableContractsUrl, {
        enabled: !!getExpandedId() && !!legalEntityIdOnTransfer,
    });

    let dataCategoryLoading = dataCategoriesQuery.isLoading;
    let legalEntityLoading = legalEntitiesQuery.isLoading;
    let groupEntityLoading = groupEntityQuery.isLoading;
    let responsibleLoading = responsibleQuery.isLoading;
    let documentsLoading = documentsQuery.isLoading;

    let legalEntityData = legalEntitiesQuery.data;
    let groupEntityData = groupEntityQuery.allGroupEntities;
    let documentsData: Array<SelectableAgreement> = documentsQuery.disclosureDocuments.map((d) => {
        return {
            id: d.id,
            name: d.name,
            contractOrDocumentType: d.documentTypeName,
            status: d.status,
            type: "document",
        };
    });

    let dataCategoriesData;
    if (dataCategoriesQuery.allDataCategories) {
        dataCategoriesData = dataCategoriesQuery.allDataCategories!.filter(function (item) {
            return selectedDataCategories.includes(item.id);
        });
    }

    const removeAgreementMutation = useDotLegalMutation(removeAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });
    const addAgreementMutation = useDotLegalMutation(addAgreementAPI, {
        onSuccess: (data) => {
            const processingActivity = data.value();
            queryClient.setQueryData(queryKey, processingActivity);
        },
    });

    const onAddAgreement = async (agreement: SelectableAgreement, sourceId: string) => {
        const agreementId = agreement.id;
        if (agreement.type === "document") {
            const newProcessingActivityModel = { ...processingActivity };
            const source = newProcessingActivityModel.sources.find((d) => d.id === sourceId)!;

            source.agreementIds.push(agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementMutation.mutateAsync, {
                processingActivityId: processingActivity.id,
                sourceId: sourceId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type == "contract") {
            const contractsOnTransfer = contractsOnSourceQuery.data!;
            contractsOnTransfer.contractIds.push(agreementId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnSourceUrl, addContractApi, {
                dataProcessorId: getExpandedId(),
                contractId: agreementId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onDeleteAgreement = async (agreement: SelectableAgreement, sourceId: string) => {
        const agreementId = agreement.id;
        if (agreement.type === "document") {
            const newProcessingActivityModel = { ...processingActivity };
            const source = newProcessingActivityModel.sources.find((d) => d.id === sourceId)!;

            source.agreementIds = source.agreementIds.filter((x) => x !== agreementId);

            await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, removeAgreementMutation.mutateAsync, {
                processingActivityId: processingActivity.id,
                sourceId: sourceId,
                saveModel: { agreementId: agreementId },
            });
        } else if (agreement.type == "contract") {
            const contractsOnTransfer = contractsOnSourceQuery.data!;
            contractsOnTransfer.contractIds = contractsOnTransfer.contractIds.filter((x) => x !== agreementId);
            await optimisticUpdate.putOnQueueAndSetQueryData(contractsOnTransfer, contractsOnSourceUrl, removeContractApi, {
                dataProcessorId: getExpandedId(),
                contractId: agreementId,
            });
        } else {
            throw new Error("Unknown agreement type " + agreement.type);
        }
    };

    const onHasAgreementChange = async (hasAgreement: boolean, sourceId: string) => {
        const newProcessingActivityModel = { ...processingActivity };
        const source = newProcessingActivityModel.sources.find((d) => d.id === sourceId)!;

        source.hasDataProcessingAgreement = hasAgreement;
        source.agreementIds = [];

        await optimisticUpdate.updateWithProcessingActivityResponse(newProcessingActivityModel, addAgreementMutation.mutateAsync, {
            processingActivityId: processingActivity.id,
            sourceId: sourceId,
            saveModel: { hasAgreement: hasAgreement },
        });
    };

    const selectableContracts: Array<SelectableAgreement> = selectableContractsQuery.data?.map((c) => toSelectableAgreement(c)) ?? [];

    const onLegalEntityOnTransferChange = () => {
        if (contractsOnSourceQuery.data) {
            const newData = { ...contractsOnSourceQuery.data };
            newData.contractIds = [];
            queryClient.setQueryData(contractsOnSourceUrl, newData);
        }
    };

    return {
        dataCategoryLoading,
        dataCategoriesData,
        legalEntityLoading,
        legalEntityData,
        responsibleLoading,
        groupEntityLoading,
        groupEntityData,
        agreementsLoading: documentsLoading || contractsOnSourceQuery.isLoading || selectableContractsQuery.isLoading,
        selectableAgreements: [...documentsData, ...selectableContracts].sort((a, b) => a.name.localeCompare(b.name)),
        getResponsibles: responsibleQuery.data,
        dataProcessorAgreementSaveModel,
        setDataProcessorAgreementSaveModel,
        sourceId,
        setSourceId,
        refetchDocuments: documentsQuery.refetch,
        getExpandedId,
        setExpandedId,
        onHasAgreementChange,
        onAddAgreement,
        onDeleteAgreement,
        selectedAgreements: [
            ...(processingActivity.sources.find((d) => d.id === getExpandedId())?.agreementIds ?? []),
            ...(contractsOnSourceQuery.data?.contractIds ?? []),
        ],
        containsNonAccessibleContracts: contractsOnSourceQuery.data?.containsNonAccessibleContacts ?? false,
        onLegalEntityOnTransferChange,
    };

    async function addAgreementAPI(data: { processingActivityId: string; sourceId: string; saveModel: AgreementSaveModel }) {
        return await put("/processingactivity/" + data.processingActivityId + "/source/" + data.sourceId + "/agreement", data.saveModel);
    }

    async function removeAgreementAPI(data: { processingActivityId: string; sourceId: string; saveModel: AgreementSaveModel }) {
        return await deleteHttp(`/processingactivity/${data.processingActivityId}/source/${data.sourceId}/agreement?agreementId=${data.saveModel.agreementId}`);
    }

    async function addContractApi(data: { dataProcessorId: string; contractId: string }) {
        return await post(`/processingActivity/Source/${data.dataProcessorId}/contracts/${data.contractId}`, null);
    }

    async function removeContractApi(data: { dataProcessorId: string; contractId: string }) {
        return await deleteHttp(`/processingActivity/Source/${data.dataProcessorId}/contracts/${data.contractId}`);
    }
}

export function useValidateSourceStep(permissions: ProcessingActivitySourcesPermissions) {
    const { translateString } = useTranslation();
    return (model: ProcessingActivityModel, responsibles: Array<UserSelectableItem> | undefined): Array<ValidationError> => {
        return getSourcesErrors(model, translateString, responsibles, permissions);
    };
}

function getSourcesErrors(
    model: ProcessingActivityModel,
    translateString: any,
    responsibles: Array<UserSelectableItem> | undefined,
    permissions: ProcessingActivitySourcesPermissions
) {
    const result: Array<ValidationError> = [];

    if (permissions) {
        if (!model.disableSources && !model.hasSources) {
            result.push(new ValidationError("noDataSharingsSources", translateString("atLeastOneSharingRequired")));
        }

        model.sources.forEach((source, index) => {
            if (!source.dataControllerId) {
                result.push(new ValidationError(`${index}legalEntityId`, translateString("noControllerError")));
            }
            if (permissions.hasDataCategoriesPermission && source.dataCategories.length === 0) {
                result.push(new ValidationError(`${index}dataCategories`, translateString("dataCategoriesRequired")));
            }
            if (source.groupEntities.length === 0) {
                result.push(new ValidationError(`${index}companies`, translateString("noCompaniesForTransferChosenError")));
            }
            if (source.groupEntities.some((l) => l === source.dataControllerId)) {
                result.push(new ValidationError(`${index}legalEntityId`, translateString("dataControllerCantBeInCompanies")));
            }

            if (permissions.hasResponsiblePermission) {
                let user = responsibles?.find((x) => x.id === source.responsible);
                if (user && !user.active) {
                    result.push(new ValidationError(`${index}inactiveUser`, translateString("userDeactivated")));
                }
            }
        });
    }

    return result;
}
